import React from 'react';

const ScrollPositionContext = React.createContext();

export const ScrollPositionProvider = ({ data, children }) => (
  <ScrollPositionContext.Provider value={data}>{children}</ScrollPositionContext.Provider>
);

export const withScrollPositionStorage = (data) => (Component) => {
  return (props) => (
    <ScrollPositionProvider data={data}>
      <Component {...props} />
    </ScrollPositionProvider>
  );
};

/**
 * Hook scrolls to saved position on the page by utilizing Context.
 *
 * Covered in https://neimanmarcus.atlassian.net/browse/OSP-2324
 *
 * @example
 * const scrollDataMap = new Map();
 *
 * const withScrollPosition = (Component) => {
 *  return (props) => (
 *    <ScrollPositionProvider data={scrollDataMap}>
 *      <Component {...props} />
 *    </ScrollPositionProvider>
 *  );
 * };
 *
 * const CustomComponent = ({ categoryId, loadMore, hasMore, isFetching }) => {
 *  const scrollAreaRef = React.useRef(null);
 *
 *  useRestoreScrollPosition(categoryId, scrollAreaRef);
 *
 *  ...code
 *
 *  return (
 *    <div style={{ height: '100%', overflowY: 'auto' }} ref={scrollAreaRef}>
 *      <InfiniteScroll
 *        data-testid="infinite-scroller"
 *        pageStart={0}
 *        initialLoad={false}
 *        loadMore={loadMore}
 *        hasMore={hasMore}
 *        useWindow={false}
 *      >
 *      {isFetching && (
 *        <Typography variant="h2" className="loader">
 *          Loading...
 *        </Typography>
 *      )}
 *    <div>
 *  );
 * };
 *
 * export default withScrollPosition(CustomComponent);
 *
 * @param {String} positionKey
 * @param {import('react').RefObject} scrollAreaRef
 */
export const useRestoreScrollPosition = (positionKey, scrollAreaRef, shouldKeepPositionKey = false) => {
  const scrollDataMap = React.useContext(ScrollPositionContext);
  React.useLayoutEffect(() => {
    const scrollToPosition = (ref, posY) => ref.current.scrollTo(0, posY);
    const scrollValue = scrollDataMap.get(positionKey) || null;
    if (scrollValue && scrollAreaRef.current) {
      scrollToPosition(scrollAreaRef, scrollValue);
      // changed to support marketing assets
      if (!shouldKeepPositionKey) {
        scrollDataMap.delete(positionKey);
      }
    }
  }, [scrollAreaRef.current, positionKey]);

  React.useEffect(() => {
    return () => {
      const offset = scrollAreaRef.current?.scrollTop;
      scrollDataMap.set(positionKey, offset);
    };
  }, []);
};
